<script lang="ts">
	import type { DateRange } from '$lib/components/pois/types';
	import type { PoiCategory, PoiData } from '../types';
	import { page } from '$app/stores';
	import NoPoi from '$assets/pois/no-poi.webp';
	import PoiCardAd from '$lib/components/advertising/PoiCardAd.svelte';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { buildPoiCardProps } from '$lib/components/pois/card/factories';
	import PoiCard from '$lib/components/pois/card/PoiCard.svelte';
	import PoiCardSkeleton from '$lib/components/pois/card/PoiCardSkeleton.svelte';
	import MichelinSwitchFilter from '$lib/components/pois/filters/components/MichelinSwitchFilter.svelte';
	import PoiListFilter from '$lib/components/pois/list/PoiListFilter.svelte';
	import { areCategoriesWithMichelinPois, getCardHeight } from '$lib/components/pois/list/utils';
	import { isHRTPoi } from '$lib/components/pois/utils';
	import { POIS_HRT } from '$lib/constants/poilist';
	import { searchExtensionTouchpoint } from '$lib/services/searchExtension';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getPoiEventPayload, trackAddItineraryEvent, trackPoiListBookEvent, trackPoiMapHoverEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import { getBookLeadPayload, metaTrackBookLead } from '$lib/services/tracking/metaPixel/tracking';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';

	const searchParams = getSearchParamsStore();

	interface Props {
		poisList: PoiData[];
		selectedPoiCategories?: PoiCategory[];
		displayAllCategories?: boolean;
		singleCategoryMode?: boolean;
		withPartner?: boolean;
		detailLink: (poi: PoiData) => string;
		isLoading?: boolean;
		showList?: boolean;
		showMichelinSwitchFilter?: boolean;
		onCardClick?: (poi: PoiData | undefined) => void;
		onAddItineraryClick?: (poi: PoiData) => void;
		onShowFilters?: () => void;
		onPoiCategoriesChange?: (categories: PoiCategory[]) => void;
		onSelectDates?: (dateRange: DateRange) => void;
		onClearDates?: () => void;
	}

	const {
		poisList,
		selectedPoiCategories = [],
		displayAllCategories = false,
		singleCategoryMode = false,
		withPartner = false,
		detailLink,
		isLoading = false,
		showList = true,
		showMichelinSwitchFilter = true,
		onCardClick,
		onAddItineraryClick,
		onShowFilters,
		onPoiCategoriesChange,
		onSelectDates,
		onClearDates,
	}: Props = $props();

	const mapLayoutContext = getMapLayoutContext();
	const { hoveredPoiId, onMichelinSelectionChange } = mapLayoutContext;

	/**
	 * Insert an ad at the beginning of the list
	 * This is needed to avoid the ad from rerendering when the list changes
	 */
	function insertAdInPoilist(
		poiList: PoiData[],
		selectedPoiCategories: PoiCategory[],
	): (PoiData | { id: string; __typename: 'ad' })[] {
		if (
			(selectedPoiCategories.length === 1 && selectedPoiCategories.includes('ACCOMMODATION'))
			|| poiList.length === 0
		) {
			return poiList;
		}

		// Use typename to differentiate between POIs and ads as it is understood by TS
		return [
			...poiList.slice(0, 2),
			// The id controls when the ad slot gets rerendered
			{
				id: `${$page.route.id}-${$searchParams.selectedAddress}-${$searchParams.poiCategories}`,
				__typename: 'ad',
			},
			...poiList.slice(2),
		];
	}

	const list = $derived(insertAdInPoilist(poisList, selectedPoiCategories));

	function handlePoiCardMouseEnter(poi: PoiData) {
		$hoveredPoiId = poi.id;
		trackPoiMapHoverEvent(poi);
	}

	function handlePoiCardMouseLeave() {
		$hoveredPoiId = null;
	}

	function handlePoiCardClick(poi: PoiData, e: Event) {
		const target = e.target as HTMLElement;
		const isCarouselControlsButtonClick
			= target instanceof HTMLButtonElement
				|| target?.parentElement?.parentElement instanceof HTMLButtonElement
				|| target.parentElement instanceof HTMLButtonElement;

		if (isCarouselControlsButtonClick) {
			return;
		}

		$hoveredPoiId = null;
		track({
			action: 'poilist_click_on_poi',
			payload: getPoiEventPayload(poi),
		});

		onCardClick?.(poi);
	}

	function handleAddItineraryClick(poi: PoiData) {
		trackAddItineraryEvent(poi);

		if (isHRTPoi(poi)) {
			searchExtensionTouchpoint.activate('poilist_click');
		}

		onAddItineraryClick?.(poi);
	}

	function handleBookClick(poi: PoiData) {
		trackPoiListBookEvent(poi);
		metaTrackBookLead(getBookLeadPayload(poi));
	}
</script>

<PoiListFilter
	{selectedPoiCategories}
	{displayAllCategories}
	{singleCategoryMode}
	{withPartner}
	onPoiCategoriesChange={event => onPoiCategoriesChange?.(event.updatedSelectedCategories)}
	{onShowFilters}
	{onSelectDates}
	{onClearDates}
/>
{#if showMichelinSwitchFilter && areCategoriesWithMichelinPois(selectedPoiCategories)}
	<MichelinSwitchFilter
		pageContext="homepage"
		class="mb-4 mx-2 shadow-smooth-block"
		onchange={isOnlyMichelin => onMichelinSelectionChange(isOnlyMichelin)}
	/>
{/if}

{#if showList}
	<ul data-testid="poilist" class="container m-0 px-2 grid">
		{#if isLoading}
			{#each { length: 6 } as _, index (index)}
				<PoiCardSkeleton {index} tag="li" withPicture={$searchParams.poiCategories?.some(value => POIS_HRT.includes(value))} />
			{/each}
		{:else}
			{#each list as poi, index (poi.id)}
				{#if poi.__typename === 'ad'}
					<li class="ad flex justify-center">
						{#key `${$page.route.id}-${$searchParams.selectedAddress?.address}-${$searchParams.poiCategories}`}
							<PoiCardAd />
						{/key}
					</li>
				{:else}
					<!-- required to trigger detailLink after page search params filters update, find better solution while refactoring -->
					{#key `${$page.route.id}-${JSON.stringify($searchParams.filters)}`}
						<PoiCard
							cardProps={buildPoiCardProps(poi, detailLink(poi), index, getCardHeight(poi))}
							onmouseenter={() => handlePoiCardMouseEnter(poi)}
							onmouseleave={handlePoiCardMouseLeave}
							onCardClick={e => handlePoiCardClick(poi, e)}
							onAddItineraryClick={() => handleAddItineraryClick(poi)}
							onBookClick={() => handleBookClick(poi)}
							isHovered={$hoveredPoiId === poi.id}
						/>
					{/key}
				{/if}
			{:else}
				<li class="wide fallback flex flex-col items-center gap-2 text-md p-4" data-testid="no-poi-result">
					<Image src={NoPoi} alt={m.poi_list__errors__no_result()} width="100px" height="100px" objectFit="contain" />
					{#if selectedPoiCategories.length === 0}
						{m.poi_list__errors__no_category()}
					{:else}
						{m.poi_list__errors__no_result()}
					{/if}
				</li>

				<li class="ad wide flex justify-center my-4">
					<PoiCardAd />
				</li>
			{/each}
		{/if}
	</ul>
{/if}

<style>
	.container {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
		grid-auto-rows: 4px;
		list-style: none;
		column-gap: 8px;
		/* Create a separate layer here to avoid flickering issues with the POI list on scroll */
		transform: translate3d(0, 0, 0);
	}

	@media screen and (min-width: 960px) {
		.container {
			grid-template-columns: repeat(2, minmax(210px, 1fr));
		}
	}

	.container :global(.xs) {
		grid-row: span 32;
		height: 124px;
	}
	.container :global(.sm) {
		grid-row: span 46;
		height: 180px;

	}
	.container :global(.md) {
		grid-row: span 56;
		height: 216px;
	}
	.container :global(.lg) {
		grid-row: span 67;
		height: 260px;
	}
	.container :global(.xl) {
		grid-row: span 70;
		height: 272px
	}
	.container :global(.ad) {
		grid-column: span 2;
		height: 272px;
		grid-row: span 70;
	}

	.container :global(.wide) {
		grid-column: 1 / -1;
	}
	.container :global(.fallback) {
		grid-row: span 40;
	}
</style>
